var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
};
import * as React from 'react';
import { createPortal } from 'react-dom';
import * as PropTypes from 'prop-types';
import { classNames, noop, canUseDOM } from '@progress/kendo-react-common';
import { Header, HeaderRow, FilterRow as TreeListFilterRow, ColumnResize, CommonDragLogic, DragClue, DropClue, tableColumnsVirtualization, readColumns, mapColumns } from '@progress/kendo-react-data-tools';
import { getNestedValue, setHeaderRowsTop, tableRowsVirtualization } from './utils/index';
import { flatData } from './utils/data-operations';
import { TreeListCell } from './cells/TreeListCell';
import { TreeListNoRecords } from './TreeListNoRecords';
import { TreeListRow } from './rows/TreeListRow';
/**
 * Represents the [KendoReact TreeList component]({% slug getstarted_treelist %}).
 */
var TreeList = /** @class */ (function (_super) {
    __extends(TreeList, _super);
    function TreeList(props) {
        var _this = _super.call(this, props) || this;
        _this.wrapperScrollLeft = 0;
        _this.wrapperScrollTop = 0;
        _this.updateOnScroll = false;
        _this.tbodyOffsetTop = 0;
        _this.toolbarHeight = 0;
        _this.prevData = [];
        _this.flattedData = [];
        _this.extendedColumn = [];
        _this.element = null;
        _this.onRowDrag = function (event) {
            if (_this.props.onRowDrag) {
                _this.props.onRowDrag.call(undefined, __assign({}, event, { target: _this }));
            }
        };
        _this.onRowDrop = function (event) {
            if (_this.props.onRowDrop) {
                _this.props.onRowDrop.call(undefined, __assign({}, event, { target: _this }));
            }
        };
        _this.columnReorder = function (prev, next, nativeEvent) {
            var _a;
            var depth = _this.extendedColumn[prev].depth;
            var end = function (index) {
                do {
                    index++;
                } while (index < _this.extendedColumn.length && _this.extendedColumn[index].depth > depth);
                return index;
            };
            var spliced = _this.extendedColumn.splice(prev, end(prev) - prev);
            (_a = _this.extendedColumn).splice.apply(_a, [prev < next ? end(next - spliced.length) : next, 0].concat(spliced));
            _this.extendedColumn.filter(function (q) { return q.declarationIndex >= 0; }).forEach(function (c, i) { return c.orderIndex = i; });
            if (_this.props.onColumnReorder) {
                var event_1 = {
                    target: _this,
                    columns: _this.columns,
                    nativeEvent: nativeEvent
                };
                _this.props.onColumnReorder.call(undefined, event_1);
            }
        };
        _this.onResize = function (index, newWidth, oldWidth, nativeEvent, end) {
            if (_this.props.onColumnResize) {
                var sumWidth = _this.extendedColumn.filter(function (c) { return c.children.length === 0; }).
                    reduce(function (acc, column) { return acc += parseFloat(String(column.width)); }, 0);
                var event_2 = {
                    columns: _this.columns,
                    totalWidth: sumWidth,
                    index: index,
                    nativeEvent: nativeEvent,
                    newWidth: newWidth,
                    oldWidth: oldWidth,
                    end: end,
                    target: _this
                };
                _this.props.onColumnResize.call(undefined, event_2);
            }
        };
        _this.handleOnScroll = function (event) {
            var scrollLeft = event.currentTarget.scrollLeft;
            var scrollTop = event.currentTarget.scrollTop;
            var _a = _this.props, columnVirtualization = _a.columnVirtualization, scrollable = _a.scrollable, _b = _a.rowHeight, rowHeight = _b === void 0 ? 0 : _b;
            var minTopChange = rowHeight;
            var minLeftChange = 0;
            var shouldForceUpdate = false;
            if (columnVirtualization && Math.abs(_this.wrapperScrollLeft - scrollLeft) > minLeftChange) {
                _this.wrapperScrollLeft = scrollLeft;
                shouldForceUpdate = true;
            }
            if (scrollable === 'virtual' && Math.abs(_this.wrapperScrollTop - scrollTop) > minTopChange) {
                _this.wrapperScrollTop = scrollTop;
                shouldForceUpdate = true;
            }
            if (shouldForceUpdate) {
                _this.updateOnScroll = true;
                _this.forceUpdate();
            }
        };
        _this.calculateSizes = function (div) {
            if (!div || _this.props.scrollable === 'none') {
                return;
            }
            var children = Array.from(div.childNodes);
            var table = children.find(function (n) { return n.nodeName === 'TABLE'; });
            var toolbar = _this.props.toolbar &&
                children.find(function (n) {
                    return n.nodeType === 1 && n.classList.contains('k-grid-toolbar');
                });
            var toolbarHeight = 0;
            if (toolbar) {
                var boxSizing = toolbar.style.boxSizing;
                toolbar.style.boxSizing = 'border-box';
                toolbarHeight = parseFloat(String(window.getComputedStyle(toolbar).height)) ||
                    toolbar.offsetHeight;
                toolbar.style.boxSizing = boxSizing;
                if (!toolbar.getAttribute('style')) {
                    toolbar.removeAttribute('style');
                }
            }
            _this.toolbarHeight = toolbarHeight;
            _this.tbodyOffsetTop = table.tBodies[0].offsetTop;
            setHeaderRowsTop(table, toolbarHeight);
        };
        _this.itemChange = function (event) {
            var itemChange = _this.props.onItemChange;
            if (event.field === _this.props.expandField) {
                var expandChange = _this.props.onExpandChange;
                if (expandChange) {
                    var expandEvent = __assign({}, _this.getArguments(event.syntheticEvent), { dataItem: event.dataItem, level: event.level, value: event.value });
                    expandChange.call(undefined, expandEvent);
                }
                return;
            }
            if (itemChange) {
                var itemChangeEvent = __assign({}, _this.getArguments(event.syntheticEvent), { dataItem: event.dataItem, level: event.level, field: event.field, value: event.value });
                itemChange.call(undefined, itemChangeEvent);
            }
        };
        _this.onHeaderSelectionChange = function (event) {
            if (_this.props.onHeaderSelectionChange) {
                var selectionEvent = {
                    field: event.field,
                    nativeEvent: event.syntheticEvent && event.syntheticEvent.nativeEvent,
                    syntheticEvent: event.syntheticEvent,
                    target: _this
                };
                _this.props.onHeaderSelectionChange.call(undefined, selectionEvent);
            }
        };
        _this.sortChange = function (syntheticEvent, sort, field) {
            _this.raiseDataEvent(_this.props.onSortChange, { sort: sort, field: field }, syntheticEvent);
        };
        _this.filterChange = function (event) {
            var filter = event.filter, field = event.field;
            _this.raiseDataEvent(_this.props.onFilterChange, { filter: filter, field: field }, event.syntheticEvent);
        };
        _this.columnMenuFilterChange = function (syntheticEvent, filter, field) {
            var onColumnMenuFilterChange = _this.props.onColumnMenuFilterChange;
            if (!onColumnMenuFilterChange) {
                return;
            }
            var event = {
                syntheticEvent: syntheticEvent,
                filter: filter,
                field: field,
                target: _this,
                nativeEvent: syntheticEvent.nativeEvent
            };
            onColumnMenuFilterChange.call(undefined, event);
        };
        _this.expandChange = function (event, dataItem, level) {
            var _a = _this.props, expandField = _a.expandField, onExpandChange = _a.onExpandChange;
            if (expandField && onExpandChange) {
                var expandEvent = __assign({}, _this.getArguments(event), { dataItem: dataItem,
                    level: level, value: _this.expanded(dataItem) });
                onExpandChange.call(undefined, expandEvent);
            }
        };
        _this.rowClick = function (e, item) {
            if (_this.props.onRowClick && e.target.nodeName === 'TD') {
                var rowClickEvent = __assign({ dataItem: item.dataItem, level: item.level }, _this.getArguments(e));
                _this.props.onRowClick.call(undefined, rowClickEvent);
            }
        };
        _this.onPageChange = function (e) {
            if (_this.props.onPageChange) {
                var event_3 = __assign({}, _this.getArguments(e.syntheticEvent), { skip: e.skip, take: e.take });
                _this.props.onPageChange.call(undefined, event_3);
            }
        };
        _this.expandedSubItems = function (dataItem) {
            var items = [];
            if (_this.expanded(dataItem) && _this.hasChildren(dataItem)) {
                items.push.apply(items, getNestedValue(_this.props.subItemsField, dataItem));
            }
            return items;
        };
        _this.expanded = function (dataItem) { return Boolean(getNestedValue(_this.props.expandField, dataItem)); };
        _this.hasChildren = function (dataItem) { return Boolean(getNestedValue(_this.props.subItemsField, dataItem)); };
        _this.dragLogic = new CommonDragLogic(_this.columnReorder, noop, noop);
        _this.columnResize = new ColumnResize(_this.onResize.bind(_this));
        return _this;
    }
    /**
     * @hidden
     */
    TreeList.prototype.componentDidUpdate = function (prevProps) {
        if (prevProps.columns !== this.props.columns) {
            this.calculateSizes(this.element);
        }
    };
    /**
     * @hidden
     */
    TreeList.prototype.componentDidMount = function () {
        this.calculateSizes(this.element);
    };
    /**
     * @hidden
     */
    TreeList.prototype.componentWillUnmount = function () {
        this.columnResize.columns = [];
        this.dragLogic.columns = [];
        this.extendedColumn = [];
        this.prevData = [];
        this.flattedData = [];
        this.updateOnScroll = false;
    };
    /**
     * @hidden
     */
    TreeList.prototype.render = function () {
        var _this = this;
        var _a = this.props, _b = _a.columns, columns = _b === void 0 ? [] : _b, filterRow = _a.filterRow, _c = _a.scrollable, scrollable = _c === void 0 ? 'scrollable' : _c, _d = _a.resizable, resizable = _d === void 0 ? false : _d, _e = _a.reorderable, reorderable = _e === void 0 ? false : _e, skip = _a.skip, take = _a.take;
        var filterable = columns.some(function (c) { return Boolean(c.filter); }) || filterRow !== undefined;
        var FilterRow = filterRow || TreeListFilterRow;
        this.extendedColumn = readColumns(columns);
        var columnsMap = mapColumns(this.extendedColumn);
        this.columnResize.columns = this.extendedColumn;
        this.columnResize.resizable = resizable;
        this.dragLogic.columns = this.extendedColumn;
        this.dragLogic.reorderable = reorderable;
        this.dragLogic.groupable = false;
        var clues = reorderable && canUseDOM && [
            createPortal(React.createElement(DropClue, { ref: this.dragLogic.refDropElementClue }), document.body),
            createPortal(React.createElement(DragClue, { ref: this.dragLogic.refDragElementClue }), document.body)
        ];
        var columnsWithColGroup = this.extendedColumn.filter(function (c) { return c.children.length === 0; });
        var header = (React.createElement(Header, { headerRow: React.createElement(HeaderRow, { sort: this.props.sort, sortable: this.props.sortable, sortChange: this.sortChange, selectionChange: this.onHeaderSelectionChange, columns: this.extendedColumn, columnsMap: columnsMap, cellRender: this.props.headerCellRender, columnResize: this.columnResize, columnMenu: this.props.columnMenu, columnMenuFilter: this.props.columnMenuFilter, columnMenuFilterChange: this.columnMenuFilterChange, pressHandler: this.dragLogic.pressHandler, dragHandler: this.dragLogic.dragHandler, releaseHandler: this.dragLogic.releaseHandler }), filterRow: filterable && React.createElement(FilterRow, { columns: columnsWithColGroup, filter: this.props.filter, filterChange: this.filterChange, sort: this.props.sort }) || undefined, columnResize: this.columnResize }));
        var wrapperStyles = this.props.style || {};
        var _f = tableColumnsVirtualization({
            enabled: this.props.columnVirtualization,
            columns: columnsWithColGroup,
            scrollLeft: this.wrapperScrollLeft,
            tableViewPortWidth: parseFloat((wrapperStyles.width || '').toString())
        }), colSpans = _f.colSpans, hiddenColumns = _f.hiddenColumns;
        var dataRow = function (item, inEdit) { return columnsWithColGroup.map(function (column, index) {
            if (hiddenColumns[index]) {
                return null;
            }
            var className = "" + (column.className ? column.className + ' ' : '')
                + ("" + (column.locked ? 'k-grid-content-sticky' : ''));
            var cellProps = {
                colSpan: colSpans[index],
                dataItem: item.dataItem,
                field: column.field,
                format: column.format,
                className: className || undefined,
                render: _this.props.cellRender,
                onChange: _this.itemChange,
                selectionChange: (_this.props.onSelectionChange) ? (function (e) {
                    _this.selectionChange(e, item);
                }) : undefined,
                level: item.level,
                expandable: column.expandable,
                expanded: _this.expanded(item.dataItem),
                hasChildren: _this.hasChildren(item.dataItem),
                onExpandChange: _this.expandChange,
                style: (column.left !== undefined) && {
                    left: column.left,
                    right: column.right,
                    borderRightWidth: column.rightBorder ? '1px' : ''
                } || {}
            };
            if (inEdit && column.editCell) {
                return (React.createElement(column.editCell, __assign({}, cellProps, { onChange: _this.itemChange, key: index })));
            }
            if (column.cell) {
                return React.createElement(column.cell, __assign({ key: index }, cellProps));
            }
            return React.createElement(TreeListCell, __assign({ key: index }, cellProps));
        }); };
        var flattedData = this.flatData;
        var total = flattedData.length;
        if (skip !== undefined && take !== undefined) {
            flattedData = flattedData.slice(skip, skip + take);
        }
        if (scrollable === 'virtual') {
            flattedData = tableRowsVirtualization({
                rows: flattedData,
                tableViewPortHeight: parseFloat((wrapperStyles.height || wrapperStyles.maxHeight || '').toString()),
                scrollTop: this.wrapperScrollTop
            });
            this.updateOnScroll = false;
        }
        var levels = flattedData.map(function (i) { return i.level; });
        var body = flattedData.length > 0 && flattedData.map(function (item) {
            var inEdit = getNestedValue(_this.props.editField, item.dataItem);
            var rowProps = {
                key: item.level.join('.'),
                level: item.level,
                levels: levels,
                dataItem: item.dataItem,
                selectedField: _this.props.selectedField,
                rowHeight: scrollable === 'virtual' ? item.height : _this.props.rowHeight,
                render: _this.props.rowRender,
                onDrop: _this.onRowDrop,
                onDrag: _this.onRowDrag,
                onClick: function (e) { return _this.rowClick(e, item); }
            };
            var EditRow = _this.props.editRow;
            var Row = _this.props.row || TreeListRow;
            var cells = dataRow(item, inEdit);
            return inEdit && EditRow ?
                React.createElement(EditRow, __assign({}, rowProps), cells) :
                React.createElement(Row, __assign({}, rowProps), cells);
        }) || (React.createElement("tr", { className: "k-grid-norecords" },
            React.createElement("td", { colSpan: columnsWithColGroup.length }, this.props.noRecords || React.createElement(TreeListNoRecords, null))));
        var sorted = function (field) {
            return _this.props.sort && _this.props.sort.some(function (descriptor) { return descriptor.field === field; });
        };
        var colGroups = (React.createElement("colgroup", { ref: function (c) { _this.columnResize.colGroupMain = c; } }, columnsWithColGroup.map(function (column, index) { return (React.createElement("col", { key: index.toString(), className: sorted(column.field) ? 'k-sorted' : undefined, style: column.width !== undefined ? { width: column.width } : undefined })); })));
        var virtualScroll = this.props.columnVirtualization || this.props.scrollable === 'virtual';
        return (React.createElement("div", { style: this.props.style, className: classNames('k-widget k-grid', this.props.className, { 'k-treelist-scrollable': scrollable !== 'none' }), ref: function (e) { return _this.element = e; }, onScroll: virtualScroll ? this.handleOnScroll : undefined },
            this.props.toolbar,
            React.createElement("table", __assign({}, (this.props.tableProps || {})),
                colGroups,
                header,
                React.createElement("tbody", null, body)),
            this.props.pager && (React.createElement(this.props.pager, { className: "k-grid-pager", total: total, skip: skip, take: take, onPageChange: this.onPageChange })),
            clues));
    };
    Object.defineProperty(TreeList.prototype, "columns", {
        get: function () {
            var shuffledColumns = this.extendedColumn.filter(function (q) { return q.declarationIndex >= 0 && q.parentIndex === -1; });
            var sanitize = function (columns) {
                columns.sort(function (a, b) { return a.declarationIndex - b.declarationIndex; });
                return columns.map(function (column) {
                    var declarationIndex = column.declarationIndex, parentIndex = column.parentIndex, depth = column.depth, colSpan = column.colSpan, rowSpan = column.rowSpan, index = column.index, kFirst = column.kFirst, groupable = column.groupable, children = column.children, props = __rest(column, ["declarationIndex", "parentIndex", "depth", "colSpan", "rowSpan", "index", "kFirst", "groupable", "children"]);
                    return children.length ? __assign({ children: sanitize(children) }, props) : props;
                });
            };
            return sanitize(shuffledColumns);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TreeList.prototype, "flatData", {
        get: function () {
            var _a = this.props, _b = _a.data, data = _b === void 0 ? [] : _b, _c = _a.rowHeight, rowHeight = _c === void 0 ? 0 : _c;
            var offsetTop = this.toolbarHeight + this.tbodyOffsetTop; // header height
            var itemDataMap = function (_item) {
                var result = { height: rowHeight, offsetTop: offsetTop };
                offsetTop += result.height;
                return result;
            };
            var flattedData = (this.updateOnScroll && this.prevData === data && this.tbodyOffsetTop > 0 && this.flattedData.length &&
                this.tbodyOffsetTop + this.toolbarHeight === this.flattedData[0].offsetTop) ?
                this.flattedData : flatData(data, this.expandedSubItems, itemDataMap);
            this.prevData = data;
            this.flattedData = flattedData;
            return flattedData;
        },
        enumerable: true,
        configurable: true
    });
    TreeList.prototype.selectionChange = function (event, item) {
        if (this.props.onSelectionChange) {
            var selectionEvent = __assign({}, this.getArguments(event.syntheticEvent), { dataItem: item.dataItem, level: item.level });
            this.props.onSelectionChange.call(undefined, selectionEvent);
        }
    };
    TreeList.prototype.raiseDataEvent = function (handler, data, syntheticEvent) {
        var dataStateChange = this.props.onDataStateChange;
        if (handler) {
            handler.call(undefined, __assign({}, this.getArguments(syntheticEvent), data));
        }
        else if (dataStateChange) {
            var dataStateEvent = __assign({}, this.getArguments(syntheticEvent), { data: __assign({}, this.getDataState(), data), dataState: __assign({}, this.getDataState(), data) });
            dataStateChange.call(undefined, dataStateEvent);
        }
    };
    TreeList.prototype.getDataState = function () {
        return {
            filter: this.props.filter,
            sort: this.props.sort
        };
    };
    TreeList.prototype.getArguments = function (syntheticEvent) {
        return {
            nativeEvent: syntheticEvent && syntheticEvent.nativeEvent,
            syntheticEvent: syntheticEvent,
            target: this
        };
    };
    /**
     * @hidden
     */
    TreeList.propTypes = {
        data: PropTypes.array,
        resizable: PropTypes.bool,
        reorderable: PropTypes.bool,
        sortable: PropTypes.oneOfType([
            PropTypes.bool,
            PropTypes.shape({
                mode: PropTypes.oneOf(['single', 'multiple']),
                allowUnsort: PropTypes.bool
            })
        ]),
        onSortChange: PropTypes.func,
        sort: PropTypes.array,
        columns: PropTypes.arrayOf(PropTypes.object),
        columnVirtualization: PropTypes.bool,
        filter: PropTypes.array,
        onFilterChange: PropTypes.func,
        filterRow: PropTypes.any,
        toolbar: PropTypes.any,
        noRecords: PropTypes.any,
        onExpandChange: PropTypes.func,
        expandField: PropTypes.string,
        subItemsField: PropTypes.string,
        selectedField: PropTypes.string,
        onSelectionChange: PropTypes.func,
        onHeaderSelectionChange: PropTypes.func,
        onRowClick: PropTypes.func,
        onItemChange: PropTypes.func,
        editField: PropTypes.string,
        scrollable: PropTypes.oneOf(['none', 'scrollable', 'virtual']),
        rowHeight: PropTypes.number,
        style: PropTypes.object,
        tableProps: PropTypes.object,
        pager: PropTypes.any,
        skip: PropTypes.number,
        take: PropTypes.number,
        onPageChange: PropTypes.func,
        onDataStateChange: PropTypes.func,
        onColumnResize: PropTypes.func,
        onColumnReorder: PropTypes.func
    };
    return TreeList;
}(React.Component));
export { TreeList };
//# sourceMappingURL=TreeList.js.map